import type { PaymentMethod, PaymentPacket } from "@shared/types";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { useInitiatePayment } from "@/api/mutations/payment";
import { usePublicConfig } from "@/api/queries/config";
import { usePaymentMethods } from "@/api/queries/payment";
import {
	BankTransferDetails,
	CoinBalanceHeader,
	CouponForm,
	MethodSelector,
	PackageSelector,
	PaymentCallbackBanner,
	PaymentSummary,
} from "@/components/payment";
import { HotFlirtsSlider } from "@/components/shared";
import { usePageMeta } from "@/hooks/use-page-meta";

const creditsSearchSchema = z.object({
	type: z.enum(["success", "error", "cancel"]).optional(),
});

export const Route = createFileRoute("/_app/credits/")({
	validateSearch: creditsSearchSchema,
	component: CreditsPage,
});

function CreditsPage() {
	const { type } = Route.useSearch();
	const success = type === "success";
	const error = type === "error" || type === "cancel";
	const { data: config } = usePublicConfig();

	usePageMeta({
		title: config ? `Credits - ${config.seo.defaultTitle}` : undefined,
	});

	const { data: methods, isLoading } = usePaymentMethods();
	const initiatePayment = useInitiatePayment();

	const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
	const [selectedPacket, setSelectedPacket] = useState<PaymentPacket | null>(null);

	const handleMethodSelect = (method: PaymentMethod) => {
		setSelectedMethod(method);
		setSelectedPacket(null);
	};

	const handlePacketSelect = (packet: PaymentPacket) => {
		setSelectedPacket(packet);
	};

	const bankTransferData = initiatePayment.data?.payment ? initiatePayment.data : null;

	const handlePurchase = () => {
		if (!selectedMethod || !selectedPacket) return;
		initiatePayment.mutate({
			provider: selectedMethod.provider,
			method: selectedMethod.method,
			amount: selectedPacket.price,
		});
	};

	const handleBackFromBankTransfer = () => {
		initiatePayment.reset();
	};

	return (
		<div className="space-y-6 max-w-3xl mx-auto">
			<h1 className="text-2xl font-bold text-text">Coins kaufen</h1>

			<PaymentCallbackBanner success={success} error={error} />

			<CoinBalanceHeader />

			{/* Gutschein -- eigenständig, immer sichtbar */}
			<CouponForm />

			{isLoading && (
				<div className="flex items-center justify-center py-12">
					<Loader2 size={32} className="animate-spin text-primary" />
				</div>
			)}

			{bankTransferData?.payment ? (
				<BankTransferDetails
					title={bankTransferData.title ?? "Kauf per Vorkasse"}
					message={bankTransferData.message ?? ""}
					info={bankTransferData.info}
					payment={bankTransferData.payment}
					onBack={handleBackFromBankTransfer}
				/>
			) : (
				methods && (
					<div className="space-y-6">
						<MethodSelector
							methods={methods}
							selectedMethod={selectedMethod?.method ?? null}
							onSelect={handleMethodSelect}
						/>

						{selectedMethod && (
							<PackageSelector
								packets={selectedMethod.packets}
								selectedPacket={selectedPacket}
								onSelect={handlePacketSelect}
							/>
						)}

						{selectedMethod && selectedPacket && (
							<PaymentSummary
								selectedPacket={selectedPacket}
								methodName={selectedMethod.method}
								providerName={selectedMethod.provider}
								onPurchase={handlePurchase}
								loading={initiatePayment.isPending}
							/>
						)}
					</div>
				)
			)}

			<HotFlirtsSlider />
		</div>
	);
}
